// Catalog.ID Client — Kit Assembler
// ============================================================================
// Assembles the per-page builders into ONE pdfmake document — the single print
// job of registration-restructure §5 — with the Catalog letterhead on EVERY
// page (the pages are meant to be separated, so no continuation stationery).
//
// The Membership Agreement is not typeset here: it is a pre-rendered asset
// (components/terms-asset.ts) that `printBooklet` fetches and appends with
// pdf-lib after pdfmake is done, stamping the kit's own footer on it so the
// whole document carries one run of page numbers (kit-merge.ts). Callers that
// pass no `terms` — Settings reprints, delegate activation, organization kits
// — get exactly the document they got before any of this existed.

import type { Content, TDocumentDefinitions } from 'pdfmake/interfaces';
import { kitCoverBackground, letterheadEveryPage } from '../letterhead';
import { loadRobotoRegular, printPdf } from '../pdf-loader';
import {
    buildBookletDefinitionWith, type BookletDefinitionOptions, type CopySheet,
} from './booklet-definition';
import { mergeKit } from './kit-merge';
import { BOOKLET_TITLE } from './shared';

export { BOOKLET_TITLE, ORGANIZATION_BOOKLET_TITLE, bookletTitle } from './shared';
export type { CopySheet, BookletDefinitionOptions } from './booklet-definition';

/** The pre-rendered agreement to append — structurally `TERMS_ASSET`. */
export interface KitTermsRef {
    url: string;
    pages: number;
}

export interface BookletPrintOptions extends BookletDefinitionOptions {
    /** Present ⇒ the agreement is fetched from `url` and appended, numbered as
     *  the tail of `options.totalPages`. Absent ⇒ nothing is fetched. */
    terms?: KitTermsRef;
    /** Page 1 is the kit's cover (`buildCoverPage`): paint the designer's cover
     *  artwork (design/coverpage.svg) under it instead of the letterhead. The
     *  three kits set it; a single-sheet document — a Settings reprint of the
     *  credentials or the Unlock Sheet — leaves it unset and keeps the
     *  letterhead on its only page. An explicit `coverBackground` wins. */
    coverArtwork?: boolean;
}

/** Assemble and print the kit — the wizard's single print job. Opens the print
 *  window via `printPdf` (blob URL + autoPrint; nothing on disk). `copySheets`
 *  are the additional recovery-sheet copies: printed after the master pages,
 *  outside their page numbering and before the appended terms. */
export async function printBooklet(
    pages: Content[][], copySheets: CopySheet[] = [], options: BookletPrintOptions = {},
): Promise<void> {
    const dd = buildBookletDefinition(pages, copySheets, options);
    const terms = options.terms;
    if (!terms) {
        await printPdf(dd);
        return;
    }
    await printPdf(dd, {
        transform: async kitPdf => mergeKit({
            kitPdf,
            termsPdf: await fetchTerms(terms.url),
            masterCount: pages.length,
            totalPages: options.totalPages ?? pages.length + terms.pages,
            title: options.title ?? BOOKLET_TITLE,
            robotoTtf: await loadRobotoRegular(),
        }),
    });
}

export function buildBookletDefinition(
    pages: Content[][], copySheets: CopySheet[] = [], options: Omit<BookletPrintOptions, 'terms'> = {},
): TDocumentDefinitions {
    const { coverArtwork, ...definitionOptions } = options;
    return buildBookletDefinitionWith(pages, letterheadEveryPage, copySheets, {
        ...definitionOptions,
        coverBackground: definitionOptions.coverBackground ?? (coverArtwork ? kitCoverBackground : undefined),
    });
}

/** The asset ships inside the bundle, so this is a same-origin static fetch;
 *  a failure here must abort the print rather than produce a kit whose page
 *  numbers promise terms that are not in it. */
async function fetchTerms(url: string): Promise<Uint8Array> {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Could not load the membership agreement (${response.status})`);
    return new Uint8Array(await response.arrayBuffer());
}
